import React from 'react';
import { Card } from 'react-bootstrap';
import { BloodworkAnalysis, FurtherConsiderations, Section } from '../../models';
import {
  HOLOBIOME_KITS,
  HolobiomeKit,
  SYSTEMIC_FORMULAS_BRAND,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import {
  HolobiomeProductTable,
  SECTION_HEADINGS,
  renderSupplementName,
} from './supplements-summary-panel';
import { sectionShortLabel } from './results-overview-panel';
import { systemicFormulasProductUrl } from '../../utils/supplementItem';
import { packHolobiomeItems } from '../../utils/pdfHolobiomePagination';

// Canonical section order, taken from the Supplements panel's ordering so the
// "from:" links read top-to-bottom the way the report is laid out.
const SECTION_ORDER: Section[] = SECTION_HEADINGS.map(h => h.section);

// sectionShortLabel is the Results Overview's own map of compact system names
// (see results-overview-panel.tsx). This panel used to keep a near-copy, which
// had already drifted — it said "Iron" where the overview box says "Iron
// Status". One map now, so a "from:" link always reads the way the box the user
// lands on does.

// Fallback Systemic Formulas product page for a Holobiome product whose real
// URL isn't in the supplement catalog yet. The preferred source is the catalog
// link (furtherConsiderations.supplementLinkByName), which carries the sheet's
// exact URL; this slug guess only runs when a product hasn't been wired into the
// catalog. The micro sign is URL-encoded to %c2%b5 (not folded to "u") to match
// the site's own scheme — e.g. "Terra µBiomic" → terra-%c2%b5biomic.
const sfProductUrlFallback = systemicFormulasProductUrl;

// Resolve a product's "learn more" URL: the catalog link from the Supplements
// tab of the sheet first, the slug guess only as a last resort.
const productUrl = (
  name: string,
  furtherConsiderations: FurtherConsiderations,
): string =>
  furtherConsiderations.supplementLinkByName(name) ?? sfProductUrlFallback(name);

// The Holobiome Roadmap is a Systemic Formulas protocol, so the whole section —
// kits AND the product table under them — is scoped to that brand. Another
// brand's products (e.g. BWH Labs' Beta Glucan) are recommendations, but
// they're not part of this roadmap, so they belong to the Supplements panel
// only.

// True when the practitioner's Supplement Settings have Systemic Formulas
// checked. An undefined list means no brand filter is in play at all (the
// callers that don't pass one), which leaves the brand enabled.
export function isSystemicFormulasEnabled(brands: string[] | undefined): boolean {
  return !brands || brands.includes(SYSTEMIC_FORMULAS_BRAND);
}

// A product some identified pattern recommends, with everything the panel needs
// to render it: the display name (as resolveSupplement returns it — the master
// supplement title, spelled the same way HOLOBIOME_KITS spells its products),
// the catalog description/link, the section(s) it came from, and the brand(s)
// whose rows recommended it.
export interface RecommendedProduct {
  name: string;
  description: string;
  // The sheet's own URL for the product, when it carries one. A kit product can
  // fall back to a Systemic Formulas slug guess; an add-on only links when the
  // sheet carries a real URL.
  link?: string;
  sections: Section[];
  brands: string[];
}

// Map each recommended supplement (by normalized display name) to the product
// and the sections whose identified patterns recommend it. Same traversal the
// Supplements panel uses (possibleRootCauseCategoriesFor → Supplements
// considerations → resolveSupplement); a product recommended through either its
// primary or its "-2" (section-specific) tag resolves to the same name, and a
// product can be recommended by patterns in more than one section (e.g. EnerGenic
// from both Immune and Glucose), which is exactly why each origin is listed.
export function collectRecommendedProducts(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, RecommendedProduct> {
  const collected = new Map<
    string,
    {
      name: string;
      description: string;
      link?: string;
      sections: Set<Section>;
      brands: Set<string>;
    }
  >();
  SECTION_HEADINGS.forEach(({ section }) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    categories.forEach(category => {
      const matches = furtherConsiderations.findConsiderations(
        category.rootCauseCategory,
        section,
        'Supplements',
        brands,
      );
      matches.forEach(consideration => {
        (consideration.items || []).forEach(item => {
          const parsed = furtherConsiderations.resolveSupplement(item);
          if (!parsed?.name) return;
          const key = normalizeSupplementName(parsed.name);
          if (!collected.has(key)) {
            collected.set(key, {
              name: parsed.name,
              description: parsed.description || '',
              link: parsed.link,
              sections: new Set(),
              brands: new Set(),
            });
          }
          collected.get(key)!.sections.add(section);
          if (consideration.brand) {
            collected.get(key)!.brands.add(consideration.brand);
          }
        });
      });
    });
  });
  const ordered = new Map<string, RecommendedProduct>();
  collected.forEach((entry, key) =>
    ordered.set(key, {
      name: entry.name,
      description: entry.description,
      link: entry.link,
      sections: SECTION_ORDER.filter(s => entry.sections.has(s)),
      brands: Array.from(entry.brands),
    }),
  );
  return ordered;
}

// Thin wrapper: normalized name → the section(s) that recommend it.
export function collectRecommendedSupplementSections(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, Section[]> {
  const sections = new Map<string, Section[]>();
  collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
  ).forEach((product, key) => sections.set(key, product.sections));
  return sections;
}

// Thin wrapper: just the set of recommended supplement names (used by tests).
export function collectRecommendedSupplementNames(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Set<string> {
  return new Set(
    collectRecommendedSupplementSections(
      bloodworkAnalysis,
      furtherConsiderations,
      brands,
    ).keys(),
  );
}

// Every product that belongs to a Holobiome kit, normalized — the membership
// test that splits recommendations into "on the roadmap" (a highlighted kit box)
// and "add-on" (the table below it).
const KIT_PRODUCT_KEYS = new Set(
  HOLOBIOME_KITS.flatMap(kit => kit.productNames.map(normalizeSupplementName)),
);

// The Systemic Formulas recommendations that AREN'T in any kit — products a
// pattern identified for this test calls for that the roadmap itself doesn't
// cover (e.g. a section-specific product). Other brands are dropped: they're
// recommendations, but not add-ons to a Systemic Formulas roadmap. Ordered by
// the first section that recommends them so the table reads top-to-bottom the
// way the report is laid out, then alphabetically within a section.
export function collectAddOnProducts(
  recommended: Map<string, RecommendedProduct>,
): RecommendedProduct[] {
  const addOns: RecommendedProduct[] = [];
  recommended.forEach((product, key) => {
    if (KIT_PRODUCT_KEYS.has(key)) return;
    // A brandless row is legacy data, not another brand — the sheet's Supplements
    // rows all carry one, so treat a missing brand as Systemic Formulas.
    const isSystemicFormulas =
      product.brands.length === 0 ||
      product.brands.includes(SYSTEMIC_FORMULAS_BRAND);
    if (isSystemicFormulas) addOns.push(product);
  });
  const firstSectionIdx = (product: RecommendedProduct): number => {
    const idx = SECTION_ORDER.indexOf(product.sections[0]);
    return idx === -1 ? SECTION_ORDER.length : idx;
  };
  return addOns.sort(
    (a, b) =>
      firstSectionIdx(a) - firstSectionIdx(b) || a.name.localeCompare(b.name),
  );
}

// Whether the report should carry a Holobiome Roadmap section at all.
//
// Purely the Supplement Settings checkbox: the roadmap IS the Systemic Formulas
// protocol, so checking or unchecking that brand shows or hides the section
// whole — kits, product table and all. It deliberately does NOT depend on this
// test's recommendations. The five kits are the protocol itself, and a
// practitioner who has the brand switched on wants to see it whether or not a
// pattern happened to land inside a kit this time; a test with no Systemic
// Formulas recommendations just shows the roadmap with nothing highlighted.
export function hasSupplementProtocolsContent(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): boolean {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return false;
  return isSystemicFormulasEnabled(brands);
}

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Jump to a report section. When omitted, the "from:" section origins render
  // as plain text instead of links (e.g. in the PDF).
  onSectionClick?: (section: Section) => void;
}

// Mix a 6-digit hex toward white — 0 keeps the color, 1 is white. Lets each kit
// derive its pale row tint and muted accent from its single color, the way the
// Results Overview derives STATUS_TINT from its status palette. Solid 6-digit
// output (no rgba) so it rasterizes cleanly through html2canvas in the PDF.
const mixWithWhite = (hex: string, amount: number): string => {
  const value = parseInt(hex.replace('#', ''), 16);
  const channel = (shift: number): string => {
    const c = (value >> shift) & 0xff;
    return Math.round(c + (255 - c) * amount)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(16)}${channel(8)}${channel(0)}`;
};

// A product row — the Results Overview section-row shape: pale tint, a 4px left
// accent in the kit color, dark readable text, no border. EVERY product ships in
// the kit, so a product no pattern pointed to is still a normal, present row
// (neutral tint, muted accent, full-strength text) rather than a grayed-out one
// that would read as "not needed". A recommended product deepens the tint, takes
// the full-strength accent, goes bold, and prints its section origins
// underneath — four signals, none of them color on the name itself.
//
// The name stays dark in BOTH states. It used to take the kit color when
// recommended, which put the card's lowest-contrast text (12px teal on pale
// teal) on the one thing people actually read, and made the same product three
// different colors between here and the two tables below. The tint and the
// accent carry the kit's identity; the name carries the words.
const productRowStyle = (
  kit: HolobiomeKit,
  recommended: boolean,
): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  // 4px, not 2: at 11px the origins line sat close enough to the name that a
  // two-section row ("Immune · Glucose") read as part of the product name.
  gap: '4px',
  padding: '6px 9px',
  borderRadius: '6px',
  border: 'none',
  borderLeft: `4px solid ${recommended ? kit.color : mixWithWhite(kit.color, 0.6)}`,
  backgroundColor: recommended ? mixWithWhite(kit.color, 0.87) : '#f7f8f9',
  color: '#212529',
  fontSize: '12px',
});

// The section(s) that recommended a product, each a jump link when
// onSectionClick is provided. A product can be recommended by more than one
// section, so all are listed. Used as line 2 inside an active kit box (white on
// the kit color) and in the Add-On Products table (blue on white).
function SectionOrigins({
  sections,
  onSectionClick,
  color = '#fff',
  style,
}: {
  sections: Section[];
  onSectionClick?: (section: Section) => void;
  color?: string;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <div
      style={{
        fontSize: '10.5px',
        letterSpacing: 'normal',
        lineHeight: 1.2,
        opacity: 0.95,
        ...style,
      }}
    >
      {sections.map((section, i) => (
        <React.Fragment key={section}>
          {i > 0 && <span> · </span>}
          {onSectionClick ? (
            <span
              role="button"
              tabIndex={0}
              onClick={() => onSectionClick(section)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSectionClick(section);
                }
              }}
              style={{ color, cursor: 'pointer', textDecoration: 'underline' }}
            >
              {sectionShortLabel(section)}
            </span>
          ) : (
            <span>{sectionShortLabel(section)}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// One kit card — the Results Overview group card shape: 255px wide, white, a 4px
// top accent in the kit color, a header divider under a kit-colored title and
// gray subtitle, then a column of section-style rows. The header counts the
// products so the card reads as "the kit contains these N", and every product
// gets a normal row whether or not a pattern pointed to it; a recommended row is
// the tinted, bold one, with its section origins as jump links underneath.
//
// The kit color appears four times per card — top bar, title, row accent, row
// tint — and nowhere else. It's structural: which kit you're in, and which rows
// matter. Product names are dark everywhere (see productRowStyle).
//
// `cardWidth` is a fixed 255px on screen; the PDF passes a percentage so three
// cards fill a portrait page's content width (see PDF_KIT_CARD_WIDTH), which is
// why this takes a CSS length as well as a number. `boxShadow` is overridable
// for the same reason — see PDF_KIT_CARD_SHADOW.
function KitCard({
  kit,
  sectionsByName,
  furtherConsiderations,
  onSectionClick,
  cardWidth = 255,
  boxShadow = '0 1px 3px rgba(0, 0, 0, 0.06)',
}: {
  kit: HolobiomeKit;
  sectionsByName: Map<string, Section[]>;
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
  cardWidth?: number | string;
  boxShadow?: string;
}): JSX.Element {
  const width = typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth;
  return (
    <div
      style={{
        flex: `0 0 ${width}`,
        width,
        border: '1px solid #e6e9ef',
        borderTop: `4px solid ${kit.color}`,
        borderRadius: '8px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        backgroundColor: '#fff',
        boxShadow,
      }}
    >
      <div
        style={{
          borderBottom: '1px solid #eef0f3',
          paddingBottom: '10px',
        }}
      >
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '15px',
            color: kit.color,
            lineHeight: 1.25,
            marginBottom: 2,
          }}
        >
          Month {kit.month}: {kit.name}
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: 1.3 }}>
          {kit.subtitle ? `${kit.subtitle} · ` : ''}
          {kit.productNames.length} products
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          gap: '6px',
        }}
      >
        {kit.productNames.map(name => {
          const sections = sectionsByName.get(normalizeSupplementName(name));
          const recommended = !!sections && sections.length > 0;
          return (
            <div key={name} style={productRowStyle(kit, recommended)}>
              {/* No textDecoration here at all — NOT 'none'. Bootstrap's
                  reboot already leaves an <a> unadorned and underlines it on
                  hover; setting it inline would win over that hover rule and
                  kill the only affordance a quiet link has. Every product in
                  every card is a link, so a permanent underline on 26 rows was
                  just noise. */}
              <a
                href={productUrl(name, furtherConsiderations)}
                target="_blank"
                rel="noopener noreferrer"
                title={`Learn more about ${name} at Systemic Formulas`}
                style={{
                  color: '#212529',
                  fontWeight: recommended ? 'bold' : 500,
                }}
              >
                {name}
              </a>
              {recommended && sections && (
                <SectionOrigins
                  sections={sections}
                  onSectionClick={onSectionClick}
                  color={kit.color}
                  style={{ fontSize: '11px' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const addOnCellStyle: React.CSSProperties = {
  padding: '5px 8px',
  borderTop: '1px solid #e9ecef',
  verticalAlign: 'top',
};

const addOnHeaderCellStyle: React.CSSProperties = {
  padding: '5px 8px',
  borderBottom: '1px solid #dee2e6',
  textAlign: 'left',
  fontWeight: 'bold',
  color: '#6c757d',
  fontSize: '11px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

// The strapline under the "Add-On Products" heading. Shared so the screen panel
// and the PDF card say the same thing.
const ADD_ON_SUBTITLE =
  'Recommended by patterns identified in this test, but not part of the Holobiome Roadmap';

function AddOnSubtitle(): JSX.Element {
  return (
    <div
      style={{
        fontSize: '12px',
        color: '#6c757d',
        lineHeight: 1.2,
        marginBottom: '8px',
      }}
    >
      {ADD_ON_SUBTITLE}
    </div>
  );
}

// The add-on table itself, with no surrounding heading or chrome. Screen wraps
// it in the collapsible "Add-On Products" block below; the PDF hands it a page's
// worth of rows at a time, so both render identical markup and can't drift.
function AddOnTable({
  addOns,
  furtherConsiderations,
  onSectionClick,
}: {
  addOns: RecommendedProduct[];
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
}): JSX.Element {
  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '12px',
        tableLayout: 'fixed',
      }}
    >
      <thead>
        <tr>
          {/* Product and Recommended Section are fixed and narrow;
              Description takes whatever's left (~68%), since it's the only
              long text. */}
          <th style={{ ...addOnHeaderCellStyle, width: '15%' }}>Product</th>
          <th style={addOnHeaderCellStyle}>Description</th>
          <th style={{ ...addOnHeaderCellStyle, width: '17%' }}>
            Recommended Section
          </th>
        </tr>
      </thead>
      <tbody>
        {addOns.map(product => {
          // A kit product can fall back to a guessed Systemic Formulas slug;
          // an add-on can be any brand, so only a real URL (the bullet's own
          // link, or the catalog's) becomes a link.
          const link =
            product.link ?? furtherConsiderations.supplementLinkByName(product.name);
          return (
            <tr key={product.name}>
              {/* Rendered by the same helper the Pattern-Identified
                  Supplements table uses, so a product reads identically in
                  both — same weight, same blue. The two tables list the same
                  add-ons a few rows apart, so matching them by construction
                  beats matching two hex values by hand. */}
              <td style={{ ...addOnCellStyle, color: '#212529' }}>
                {renderSupplementName({ name: product.name, link })}
              </td>
              <td style={{ ...addOnCellStyle, color: '#495057' }}>
                {product.description}
              </td>
              <td style={addOnCellStyle}>
                <SectionOrigins
                  sections={product.sections}
                  onSectionClick={onSectionClick}
                  color="#007bff"
                  style={{ fontSize: '12px' }}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// "Add-On Products" — the recommendations that fall outside the roadmap. Same
// data as a highlighted kit box (product, recommending section(s)) plus the
// catalog description, laid out as a table because these products have no kit
// to group them into. Renders nothing when every recommendation is on the
// roadmap.
//
// These products appear again in the by-pattern table below, which lists every
// Systemic Formulas recommendation together — so this table overlaps it. It
// stays because the two answer different questions ("what do I buy on top of
// the kits" vs "what did each pattern call for, and when does it start"), and
// it belongs to the roadmap in a way the by-pattern table doesn't. The
// checkbox on the heading is the escape hatch when the overlap isn't wanted:
// the heading stays put when it's off, so the table is one click back.
function AddOnProductsTable({
  addOns,
  furtherConsiderations,
  show,
  onToggle,
  onSectionClick,
}: {
  addOns: RecommendedProduct[];
  furtherConsiderations: FurtherConsiderations;
  show: boolean;
  onToggle: () => void;
  onSectionClick?: (section: Section) => void;
}): JSX.Element | null {
  if (addOns.length === 0) return null;
  return (
    <div
      style={{
        width: '100%',
        marginTop: '4px',
        borderTop: '1px solid #dee2e6',
        paddingTop: '10px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#212529' }}>
          Add-On Products
        </div>
        {/* Boxed, unlike the "Include in PDF" labels in the card headers: those
            sit on the dark navbar strip, which frames them on its own. This one
            is loose on a white body next to a heading, where an unboxed
            checkbox reads as part of the text above it rather than as a
            control. */}
        <label
          htmlFor="show-add-on-products"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            margin: 0,
            padding: '3px 9px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            backgroundColor: '#f8f9fa',
            fontSize: '12px',
            fontWeight: 'normal',
            color: '#6c757d',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          title="Show the add-on products as their own table. They're also listed, with their pattern and start month, in the table below."
        >
          <span style={{ marginRight: '6px' }}>Show this table</span>
          <input
            id="show-add-on-products"
            type="checkbox"
            className="lsm-styled-checkbox"
            checked={show}
            onChange={onToggle}
          />
        </label>
      </div>
      {!show ? null : (
      <>
      <AddOnSubtitle />
      <AddOnTable
        addOns={addOns}
        furtherConsiderations={furtherConsiderations}
        onSectionClick={onSectionClick}
      />
      </>
      )}
    </div>
  );
}

// The panel legend. Each kit is bought whole, so a highlighted product means
// "this test's patterns point here", never "the other products are unnecessary
// or left out". Stated once above the cards rather than repeated on each one.
function RoadmapLegend(): JSX.Element {
  return (
    <div
      style={{
        fontSize: '12px',
        color: '#6c757d',
        lineHeight: 1.35,
        marginBottom: '12px',
      }}
    >
      Each month's kit ships with <strong>all</strong> of its products.
      Highlighted products are the ones a pattern identified in this test points
      to, listed with the section it came from.
    </div>
  );
}

// The Holobiome Roadmap body — everything Systemic Formulas in one place.
//
// Top: a Results-Overview-style row of the five kits. Every product in a kit is
// listed as a normal row; the ones a pattern identified for this test points to
// are tinted and bold, and carry their recommending section(s) as jump links, so
// the practitioner can see at a glance which kits the recommendations cluster
// into and where they came from.
//
// Then: "Add-On Products", the recommendations that aren't in any kit — what to
// buy on top of the boxes.
//
// Last: HolobiomeProductTable, the Systemic Formulas half of what used to live
// in the Supplements panel — every recommended product, kit and add-on alike,
// grouped by the system that called for it, with the pattern it came from and
// the month it starts.
export const SupplementProtocolsPanel: React.FC<Props> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  onSectionClick,
}) => {
  // On by default, so the section reads the way it did before the by-pattern
  // table joined it. View state only — it isn't saved with the test or the
  // profile, so it resets when the report is reopened.
  const [showAddOns, setShowAddOns] = React.useState(true);

  // Below the hook, not above it: the early returns must not change how many
  // hooks this component calls.
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  // Same brand gate as hasSupplementProtocolsContent, so a caller that renders
  // the panel without checking first still respects the Systemic Formulas
  // checkbox.
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return null;

  const recommended = collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  const sectionsByName = new Map<string, Section[]>();
  recommended.forEach((product, key) => sectionsByName.set(key, product.sections));

  return (
    <>
      <RoadmapLegend />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          marginBottom: '16px',
        }}
      >
        {HOLOBIOME_KITS.map(kit => (
          <KitCard
            key={kit.key}
            kit={kit}
            sectionsByName={sectionsByName}
            furtherConsiderations={furtherConsiderations}
            onSectionClick={onSectionClick}
          />
        ))}
      </div>
      <AddOnProductsTable
        addOns={collectAddOnProducts(recommended)}
        furtherConsiderations={furtherConsiderations}
        show={showAddOns}
        onToggle={() => setShowAddOns(v => !v)}
        onSectionClick={onSectionClick}
      />
      <HolobiomeProductTable
        bloodworkAnalysis={bloodworkAnalysis}
        furtherConsiderations={furtherConsiderations}
        enabledSupplementBrands={enabledSupplementBrands}
      />
    </>
  );
};

// ───────────────────────── PDF ─────────────────────────
//
// The kit boxes and the Add-On Products table, rendered for the Analysis PDF.
// The third block of the on-screen section — Pattern-Identified Supplements —
// is SupplementsSummaryPanel in its 'systemicFormulas' scope, which paginates
// itself; the PDF container renders it right after this one, so the printed
// roadmap reads in the same order as the screen.
//
// Two things differ from the screen. The cards are narrower (three across a
// portrait page instead of a wrapping row of five), and the rows of cards and
// the table rows are packed into explicit pages here rather than left to
// html2pdf — see pdfHolobiomePagination.ts for why this PDF never trusts
// page-break-inside.

// Card titles. Each block gets its own so three consecutive roadmap pages don't
// all carry the same header.
const PDF_KITS_TITLE = 'Holobiome Roadmap — Systemic Formulas';
const PDF_ADD_ONS_TITLE = 'Holobiome Roadmap — Add-On Products';
// Passed to SupplementsSummaryPanel by the PDF container for the third block.
export const PDF_PATTERN_TABLE_TITLE =
  'Holobiome Roadmap — Pattern-Identified Supplements';

// Space above a PDF card that opens a fresh page, on top of the 10px top margin
// every .card carries (index.css) — so a card header sits 16px below the page
// edge, the same as every section title. It used to be 28, which read as a much
// deeper gap on the roadmap pages than anywhere else in the report. The
// container uses this for the blocks it opens itself (the roadmap, the add-on
// table, the Supplements panel); PdfCard uses it for its own continuation
// pages. A big buffer was protection against html2canvas's page-grid drift,
// which is now zero — see PDF_CAPTURE_WIDTH_PX in bloodwork-analysis-pdf.tsx.
export const PDF_CARD_TOP_SPACER = 6;

// Layout: three cards per row, so the five kits print 3 + 2 on ONE page. Each
// card is a third of the card body less its share of the gaps — percentages
// rather than a pixel width because the body is narrower than it looks: the
// 801px page loses 40px to .pdf-page's side padding, 2px to the card border and
// 30px to .card-body's padding, leaving ~729px. That works out to ~234px a
// card. A fixed 248px was what printed Month 3 clipped at the right edge (3 x
// 248 + 2 gaps = 772 against 729); going two-across fixed the clipping but made
// the cards 357px wide and pushed Month 5 onto a second page. Five across (the
// screen layout) would be 1339px.
const PDF_KIT_GAP = 14;
const PDF_KITS_PER_ROW = 3;
const PDF_KIT_CARD_WIDTH = `calc(${(100 / PDF_KITS_PER_ROW).toFixed(4)}% - ${(
  (PDF_KIT_GAP * (PDF_KITS_PER_ROW - 1)) /
  PDF_KITS_PER_ROW
).toFixed(2)}px)`;
// The card's drop shadow is a screen affordance and html2canvas renders it
// wrong: instead of a soft outer shadow it paints a hard rgba(0,0,0,0.06) band
// several px wide INSIDE the card's right padding — the grey stripe that showed
// up beside the products in the right-hand kits. Print gets no shadow.
const PDF_KIT_CARD_SHADOW = 'none';

// Height estimates in html2pdf canvas px. Same budget the Supplements panel
// uses (its PDF_PAGE_CAPACITY / PDF_CARD_OVERHEAD), so the two roadmap halves
// break on the same grid. Erring high only costs whitespace; erring low puts
// content in the footer.
const PDF_PAGE_CAPACITY = 900;
const PDF_CARD_OVERHEAD = 76;
const PDF_LEGEND_HEIGHT = 46;
const PDF_ADD_ON_INTRO_HEIGHT = 46;
// Card padding + borders + the "Month N: Name" header block + the gap below it.
const PDF_KIT_CARD_BASE = 100;
// One product row and the gap under it...
const PDF_KIT_PRODUCT_ROW = 35;
// ...plus the section-origins line a recommended row adds beneath the name.
const PDF_KIT_ORIGINS_LINE = 20;
// Add-on table: header row, then a row's vertical padding plus a line per line
// of wrapped description. The description column is ~68% of 772px, which fits
// roughly 90 characters at 12px.
const PDF_ADD_ON_HEADER_ROW = 26;
const PDF_ADD_ON_ROW_BASE = 22;
const PDF_ADD_ON_LINE = 17;
const PDF_ADD_ON_CHARS_PER_LINE = 90;

const estimateKitCardHeight = (
  kit: HolobiomeKit,
  sectionsByName: Map<string, Section[]>,
): number =>
  kit.productNames.reduce((height, name) => {
    const sections = sectionsByName.get(normalizeSupplementName(name));
    const recommended = !!sections && sections.length > 0;
    return height + PDF_KIT_PRODUCT_ROW + (recommended ? PDF_KIT_ORIGINS_LINE : 0);
  }, PDF_KIT_CARD_BASE);

const estimateAddOnRowHeight = (product: RecommendedProduct): number =>
  PDF_ADD_ON_ROW_BASE +
  Math.max(
    1,
    Math.ceil((product.description || '').length / PDF_ADD_ON_CHARS_PER_LINE),
  ) *
    PDF_ADD_ON_LINE;

// Chunk the five kits into rows (Months 1-2, 3-4, then 5 on its own). A row is
// the atom the packer moves, so cards never end up split across a page boundary
// from their rowmates.
function kitRows(): HolobiomeKit[][] {
  const rows: HolobiomeKit[][] = [];
  for (let i = 0; i < HOLOBIOME_KITS.length; i += PDF_KITS_PER_ROW) {
    rows.push(HOLOBIOME_KITS.slice(i, i + PDF_KITS_PER_ROW));
  }
  return rows;
}

// Everything the PDF renderer and the container's height estimate both need,
// computed once from the same inputs so the estimate can never describe a
// different layout than the one that prints.
function buildRoadmapPdfPages(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  enabledSupplementBrands: string[] | undefined,
) {
  const recommended = collectRecommendedProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  const sectionsByName = new Map<string, Section[]>();
  recommended.forEach((product, key) =>
    sectionsByName.set(key, product.sections),
  );

  const rows = kitRows();
  const kitPages = packHolobiomeItems<HolobiomeKit[]>({
    items: rows,
    heights: rows.map(row =>
      Math.max(...row.map(kit => estimateKitCardHeight(kit, sectionsByName))),
    ),
    pageCapacity: PDF_PAGE_CAPACITY,
    cardOverhead: PDF_CARD_OVERHEAD,
    firstPageExtra: PDF_LEGEND_HEIGHT,
    itemGap: PDF_KIT_GAP,
  });

  const addOns = collectAddOnProducts(recommended);
  const addOnPages = packHolobiomeItems<RecommendedProduct>({
    items: addOns,
    heights: addOns.map(estimateAddOnRowHeight),
    pageCapacity: PDF_PAGE_CAPACITY,
    // The table header repeats on every page, so it's page overhead, not a row.
    cardOverhead: PDF_CARD_OVERHEAD + PDF_ADD_ON_HEADER_ROW,
    firstPageExtra: PDF_ADD_ON_INTRO_HEIGHT,
  });

  return { sectionsByName, kitPages, addOnPages };
}

// How tall the roadmap's LAST printed page is, for the container's
// disclaimer-placement math. 0 when the section isn't in the PDF at all.
export function estimatedHolobiomeRoadmapPdfHeight(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  enabledSupplementBrands: string[] | undefined,
): number {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return 0;
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return 0;
  const { kitPages, addOnPages } = buildRoadmapPdfPages(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
  const pages = addOnPages.length > 0 ? addOnPages : kitPages;
  return pages.length > 0 ? pages[pages.length - 1].height : 0;
}

// One PDF card — one printed page.
//
// `forceBreak` and `continued` are deliberately separate. Every card here gets a
// page of its own, so all but the very first force a break; but only a card that
// carries the SPILLOVER of the block above it says "(cont.)". The first add-on
// page forces a break (the kits printed above it) without being a continuation
// of anything.
//
// The break is followed by the same 28px top buffer the container puts before
// the first card: a forced break lands a hair above the true page edge, and
// without the spacer the card header gets clipped.
function PdfCard({
  title,
  forceBreak,
  continued,
  children,
}: {
  title: string;
  forceBreak: boolean;
  continued: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      {forceBreak && (
        <>
          <div className="html2pdf__page-break" />
          <div style={{ height: PDF_CARD_TOP_SPACER }} aria-hidden />
        </>
      )}
      <Card className="disclaimer-panel mb-5">
        <Card.Header className="py-1">
          {continued ? `${title} (cont.)` : title}
        </Card.Header>
        <Card.Body
          className="disclaimer-body note-card-body"
          style={{ paddingBottom: 12 }}
        >
          {children}
        </Card.Body>
      </Card>
    </>
  );
}

interface PdfProps {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
}

// The kit boxes and the Add-On Products table for the PDF.
//
// The add-on table always prints. Its on-screen "Show this table" checkbox is
// view state in SupplementProtocolsPanel — never saved with the test, and in a
// different window from this one — so there's nothing here to read; "Include in
// PDF" on the section header is what takes the whole roadmap out.
export const HolobiomeRoadmapPdf: React.FC<PdfProps> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
}) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;
  if (!isSystemicFormulasEnabled(enabledSupplementBrands)) return null;

  const { sectionsByName, kitPages, addOnPages } = buildRoadmapPdfPages(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );

  // The container already emitted the break and spacer that put the first kit
  // page on a fresh page, so that one card alone doesn't force its own.
  return (
    <>
      {kitPages.map((page, i) => (
        <PdfCard
          key={`holobiome-kits-${i}`}
          title={PDF_KITS_TITLE}
          forceBreak={i > 0}
          continued={i > 0}
        >
          {i === 0 && <RoadmapLegend />}
          {page.items.map(row => (
            <div
              key={row.map(kit => kit.key).join('-')}
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                gap: `${PDF_KIT_GAP}px`,
                alignItems: 'flex-start',
                marginBottom: `${PDF_KIT_GAP}px`,
              }}
            >
              {row.map(kit => (
                <KitCard
                  key={kit.key}
                  kit={kit}
                  sectionsByName={sectionsByName}
                  furtherConsiderations={furtherConsiderations}
                  cardWidth={PDF_KIT_CARD_WIDTH}
                  boxShadow={PDF_KIT_CARD_SHADOW}
                />
              ))}
            </div>
          ))}
        </PdfCard>
      ))}
      {/* The kits always print above the add-ons, so even the first add-on page
          forces a break — it just isn't a "(cont.)" of anything. */}
      {addOnPages.map((page, i) => (
        <PdfCard
          key={`holobiome-add-ons-${i}`}
          title={PDF_ADD_ONS_TITLE}
          forceBreak
          continued={i > 0}
        >
          {i === 0 && <AddOnSubtitle />}
          <AddOnTable
            addOns={page.items}
            furtherConsiderations={furtherConsiderations}
          />
        </PdfCard>
      ))}
    </>
  );
};
